import React from "react";
import { useCosmicParallax } from "@/hooks/useCosmicParallax";

/**
 * DEEP VOID BACKDROP
 * Philosophy: 90% void, 10% signal. Darkness creates the value of the signal.
 *
 *   Layer 1 (deep void): pure black with a single, subtle violet ember at center
 *   Layer 2 (atmospheric fog): faint blurred haze, slow parallax
 *   Layer 3 (distant stars): sparse twinkling points, far away, rare
 *
 * No nebula image. No plexus clutter. Just void with a signal pulse.
 */
const CosmicBackdrop = () => {
  const { mx, my, sy } = useCosmicParallax();

  const deepTx = mx * 4;
  const deepTy = my * 4 + sy * 0.04;
  const midTx = mx * 8;
  const midTy = my * 8;

  return (
    <>
      {/* Layer 1 — Deep void (true black with a single faint signal ember) */}
      <div
        className="fixed inset-0 z-0 pointer-events-none void-bg"
        style={{
          transform: `translate3d(${deepTx}px, ${deepTy}px, 0)`,
          willChange: "transform",
        }}
      />

      {/* Layer 2 — Atmospheric fog (very faint, slow drift) */}
      <div
        className="fixed inset-0 z-0 pointer-events-none void-fog"
        style={{
          transform: `translate3d(${midTx}px, ${midTy}px, 0)`,
          willChange: "transform",
        }}
      />

      {/* Layer 3 — Rare distant stars */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden void-stars" aria-hidden="true">
        <FaintStars count={42} />
      </div>
    </>
  );
};

/** Sparse, slow-twinkling stars — true negative-space stars, not clutter */
const FaintStars = ({ count = 42 }) => {
  const stars = React.useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: Math.random() * 1.2 + 0.4,
        delay: Math.random() * 8,
        duration: 5 + Math.random() * 6,
        opacity: 0.3 + Math.random() * 0.4,
      })),
    [count]
  );

  return (
    <>
      {stars.map((s) => (
        <span
          key={s.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            opacity: s.opacity,
            boxShadow: `0 0 ${s.size * 2}px rgba(255,255,255,${s.opacity * 0.8})`,
            animation: `void-twinkle ${s.duration}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
    </>
  );
};

export default CosmicBackdrop;
